import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import { useUserProfile } from "../../contexts/UserProfileContext";

interface Template {
  id: string;
  name: string;
  description: string | null;
}

export default function PacklistTemplatesAdmin() {
  const { profile } = useUserProfile();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [newTemplate, setNewTemplate] = useState({ name: "", description: "" });
  const [editMode, setEditMode] = useState<string | null>(null);
  const [editData, setEditData] = useState({ name: "", description: "" });

  useEffect(() => {
    if (profile?.role !== "admin") return;

    const fetchTemplates = async () => {
      const { data, error } = await supabase
        .from("packlist_templates")
        .select("*")
        .order("name");

      if (error) {
        console.error("Fehler beim Laden der Templates:", error.message);
        return;
      }
      setTemplates(data || []);
      setLoading(false);
    };

    fetchTemplates();
  }, [profile]);

  const handleAdd = async () => {
    if (!newTemplate.name) return;

    const { data, error } = await supabase
      .from("packlist_templates")
      .insert([newTemplate])
      .select();

    if (error) {
      alert("Fehler beim Erstellen: " + error.message);
    } else {
      setTemplates([...templates, ...(data || [])]);
      setNewTemplate({ name: "", description: "" });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Wirklich löschen?")) return;

    const { error } = await supabase
      .from("packlist_templates")
      .delete()
      .eq("id", id);

    if (error) {
      alert("Fehler beim Löschen: " + error.message);
    } else {
      setTemplates(templates.filter((t) => t.id !== id));
    }
  };

  const startEdit = (template: Template) => {
    setEditMode(template.id);
    setEditData({
      name: template.name,
      description: template.description || "",
    });
  };

  const cancelEdit = () => {
    setEditMode(null);
  };

  const saveEdit = async (id: string) => {
    const { error } = await supabase
      .from("packlist_templates")
      .update(editData)
      .eq("id", id);

    if (error) {
      alert("Fehler beim Speichern: " + error.message);
    } else {
      const updated = templates.map((t) =>
        t.id === id ? { ...t, ...editData } : t
      );
      setTemplates(updated);
      setEditMode(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto mt-10 p-6">
      <h1 className="text-2xl font-bold text-sky-400 mb-6">
        📋 Packlisten-Vorlagen verwalten
      </h1>

      {/* Neue Vorlage */}
      <div className="bg-[#161b27] border border-[#2a3347] rounded-xl p-4 mb-6">
        <h2 className="font-semibold mb-2 text-slate-100">➕ Neue Vorlage erstellen</h2>
        <div className="flex flex-col gap-3">
          <input
            type="text"
            placeholder="Name (z. B. Strandurlaub)"
            value={newTemplate.name}
            onChange={(e) =>
              setNewTemplate({ ...newTemplate, name: e.target.value })
            }
            className="border border-[#2a3347] bg-[#0d1117] text-slate-100 p-2 rounded outline-none focus:border-sky-400 placeholder:text-slate-600"
          />
          <input
            type="text"
            placeholder="Beschreibung (optional)"
            value={newTemplate.description}
            onChange={(e) =>
              setNewTemplate({ ...newTemplate, description: e.target.value })
            }
            className="border border-[#2a3347] bg-[#0d1117] text-slate-100 p-2 rounded outline-none focus:border-sky-400 placeholder:text-slate-600"
          />
          <button
            onClick={handleAdd}
            className="bg-sky-500 text-white px-4 py-2 rounded hover:bg-sky-600 transition-colors"
          >
            Vorlage erstellen
          </button>
        </div>
      </div>

      {/* Bestehende Templates */}
      <h2 className="font-semibold mb-2 text-slate-100">📑 Bestehende Vorlagen</h2>
      {loading ? (
        <p className="text-slate-400">Lade Vorlagen...</p>
      ) : templates.length === 0 ? (
        <p className="text-slate-500">Keine Vorlagen vorhanden.</p>
      ) : (
        <ul className="space-y-3">
          {templates.map((tpl) => (
            <li
              key={tpl.id}
              className="flex justify-between items-start bg-[#1e2639] border border-[#2a3347] p-3 rounded flex-col sm:flex-row sm:items-center"
            >
              {editMode === tpl.id ? (
                <div className="flex flex-col gap-2 w-full sm:flex-row sm:items-center sm:gap-4">
                  <input
                    type="text"
                    className="border border-[#2a3347] bg-[#0d1117] text-slate-100 p-1 rounded w-full sm:w-48 outline-none focus:border-sky-400"
                    value={editData.name}
                    onChange={(e) =>
                      setEditData({ ...editData, name: e.target.value })
                    }
                  />
                  <input
                    type="text"
                    className="border border-[#2a3347] bg-[#0d1117] text-slate-100 p-1 rounded w-full outline-none focus:border-sky-400"
                    value={editData.description}
                    onChange={(e) =>
                      setEditData({ ...editData, description: e.target.value })
                    }
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => saveEdit(tpl.id)}
                      className="bg-sky-500 text-white px-3 py-1 rounded hover:bg-sky-600 transition-colors"
                    >
                      Speichern
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="bg-[#1e2639] border border-[#2a3347] text-slate-300 px-4 py-2 rounded hover:bg-[#2a3347] transition-colors"
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col flex-1">
                  <div className="font-semibold text-slate-100">{tpl.name}</div>
                  {tpl.description && (
                    <div className="text-sm text-slate-400 italic">
                      {tpl.description}
                    </div>
                  )}
                  <div className="mt-1">
                    <Link
                      to={`/admin/packlists/templates/${tpl.id}/items`}
                      className="text-sm text-sky-400 hover:underline"
                    >
                      Items verwalten
                    </Link>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
